import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Newspaper, ExternalLink, ArrowRight } from "lucide-react";
import { apiGet } from "@/lib/api";
import { SectionLabel } from "@/components/common/states";

function timeAgo(pub) {
  if (!pub) return "";
  const d = new Date(pub);
  if (isNaN(d)) return "";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.round(diff / 60))} min ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)} hr ago`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function NewsWire() {
  const [items, setItems] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    apiGet("/news")
      .then((d) => { setItems(d.items || []); if (d.fallback) setFailed(true); })
      .catch(() => { setItems([]); setFailed(true); });
  }, []);

  return (
    <section className="ec-container py-20 sm:py-24" data-testid="news-wire">
      <div className="flex items-end justify-between mb-10 gap-4">
        <div>
          <SectionLabel>Market News</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl text-foreground">Pune Real Estate News</h2>
          <p className="mt-2 text-muted-foreground text-sm">Live headlines on Pune's residential market, launches and infrastructure.</p>
        </div>
        <Link to="/guides" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-primary hover:gap-2 transition-all">
          Buyer guides <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>

      {items === null ? (
        <div className="grid gap-6 sm:grid-cols-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-40 rounded-md border border-border bg-muted animate-pulse" />)}
        </div>
      ) : items.length > 0 ? (
        <div className="grid gap-6 sm:grid-cols-3">
          {items.slice(0, 6).map((n, i) => (
            <a key={i} href={n.link} target="_blank" rel="noreferrer" data-testid={`news-item-${i}`}
              className="group block rounded-md border border-border bg-white p-6 hover:shadow-lg hover:border-primary/40 transition-all">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-accent mb-3">
                <Newspaper className="h-3.5 w-3.5" aria-hidden /> {n.source}
              </div>
              <h3 className="font-display text-lg text-foreground leading-snug group-hover:text-primary transition-colors">{n.title}</h3>
              {n.summary && <p className="text-sm text-muted-foreground mt-2 line-clamp-3">{n.summary}</p>}
              <div className="flex items-center justify-between mt-4 text-xs text-muted-foreground">
                <span>{timeAgo(n.published)}</span>
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              </div>
            </a>
          ))}
        </div>
      ) : (
        <div className="rounded-md border border-border bg-white p-8 text-center" data-testid="news-fallback">
          <Newspaper className="h-8 w-8 text-muted-foreground mx-auto mb-3" aria-hidden />
          <p className="text-muted-foreground">Live market news is momentarily unavailable. Explore our
            <Link to="/guides" className="text-primary underline underline-offset-4 mx-1">buyer guides</Link>
            for Pune real-estate insights in the meantime.</p>
        </div>
      )}
    </section>
  );
}
